const { getElement, getElements, formatMoney } = window.BAO.Utils;

class VolumeBoostPricing extends window.BAO.CustomElement() {
  static get requiredAttributes() {
    return [
      "data-price-inc-vat",
      "data-vat-rate",
      "data-translation-get-each",
      "data-translation-inc-vat",
      "data-translation-exc-vat",
      "data-translation-discount-heading",
    ];
  }

  static selectors = {
    price: ".hulkapps-price",
    discountHeading: "thead th",
  };

  setupListeners() {
    super.setupListeners();

    this.priceObserver = new MutationObserver(() => {
      this.handlePriceElementRender();
      this.handleDiscountHeadingRender();
    });

    this.priceObserver.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.handlePriceElementRender();
    this.handleDiscountHeadingRender();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.priceObserver?.disconnect();
  }

  handleDiscountHeadingRender() {
    if (!this.dataset.translationDiscountHeading) return;

    const headingEl = Array.from(
      getElements(this.constructor.selectors.discountHeading, {
        context: this,
      }),
    ).find((th) => th.textContent?.trim().toLowerCase() === "discount");

    if (!headingEl) return;
    if (headingEl.textContent === this.dataset.translationDiscountHeading)
      return;

    headingEl.textContent = this.dataset.translationDiscountHeading;
  }

  handlePriceElementRender() {
    if (this.getAttribute("aria-hidden") === "true") return;

    const priceEls = getElements(this.constructor.selectors.price, {
      context: this,
    });

    priceEls.forEach((priceEl) => {
      if (priceEl.querySelector(".vb-Pdp_Price")) return;

      const discountText = priceEl.textContent;
      const discountTextLowercased = discountText?.toLowerCase();
      const quantityText = getElement("td", {
        context: priceEl?.closest("tr"),
      })?.textContent;
      if (!discountText || !quantityText) return;

      let rowType = "fixed-price";

      // When a "(" and the word "each" appear in the string the first value is either a percentage or price discount
      if (
        discountTextLowercased.includes("(") &&
        discountTextLowercased.includes("each")
      ) {
        if (discountTextLowercased.includes("%")) {
          rowType = "percentage-discount";
        } else if (
          discountTextLowercased.includes("£") ||
          discountTextLowercased.includes("€")
        ) {
          rowType = "price-discount";
        }
      } else if (discountTextLowercased.includes("% off")) {
        rowType = "percentage-discount";
      }

      if (rowType === "fixed-price") {
        this.renderFixedPrice(priceEl, discountText);
      }

      if (rowType === "percentage-discount") {
        this.renderPercentageDiscount(priceEl, discountText);
      }

      if (rowType === "price-discount") {
        this.renderPriceDiscount(priceEl, discountText);
      }
    });
  }

  renderFixedPrice(priceEl, discountText) {
    if (!priceEl || !discountText) return;

    const priceIncVat = parseInt(
      discountText.split("(")?.[0]?.replace(/\D/g, ""),
    );
    const priceExcVat = Math.ceil(priceIncVat / this.vatRate);

    priceEl.innerHTML = /* html */ `
      <span class="vb-Pdp_Price vb-Pdp_Price-incVat">${
        this.dataset.translationGetEach
      } ${formatMoney(priceIncVat)} ${this.dataset.translationIncVat}</span>
      <span class="vb-Pdp_Price vb-Pdp_Price-excVat fz-12_140">${
        this.dataset.translationGetEach
      } ${formatMoney(priceExcVat)} ${this.dataset.translationExcVat}</span>
    `;
  }

  renderPercentageDiscount(priceEl, discountText) {
    if (!priceEl || !discountText) return;

    const percentage =
      (100 - parseInt(discountText?.split("%")?.[0]?.replace(/\D/g, ""))) / 100;
    const priceIncVat = Math.ceil(
      parseInt(this.dataset.priceIncVat) * percentage,
    );
    const priceExcVat = Math.ceil(priceIncVat / this.vatRate);

    priceEl.innerHTML = /* html */ `
      <span class="vb-Pdp_Price vb-Pdp_Price-incVat">${
        this.dataset.translationGetEach
      } ${formatMoney(priceIncVat)} ${this.dataset.translationIncVat}</span>
      <span class="vb-Pdp_Price vb-Pdp_Price-excVat fz-12_140">${
        this.dataset.translationGetEach
      } ${formatMoney(priceExcVat)} ${this.dataset.translationExcVat}</span>
    `;
  }

  renderPriceDiscount(priceEl, discountText) {
    if (!priceEl || !discountText) return;

    const discountAmount = parseInt(
      discountText.split("(")?.[0]?.replace(/\D/g, ""),
    );
    const priceIncVat = parseInt(this.dataset.priceIncVat) - discountAmount;
    const priceExcVat = Math.ceil(priceIncVat / this.vatRate);

    priceEl.innerHTML = /* html */ `
      <span class="vb-Pdp_Price vb-Pdp_Price-incVat">${
        this.dataset.translationGetEach
      } ${formatMoney(priceIncVat)} ${this.dataset.translationIncVat}</span>
      <span class="vb-Pdp_Price vb-Pdp_Price-excVat fz-12_140">${
        this.dataset.translationGetEach
      } ${formatMoney(priceExcVat)} ${this.dataset.translationExcVat}</span>
    `;
  }

  get vatRate() {
    return parseFloat(this.dataset.vatRate);
  }
}

if (!window.customElements.get("volume-boost-pricing")) {
  window.customElements.define("volume-boost-pricing", VolumeBoostPricing);
}